import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { INTERNAL_CONVERT_PAIRS, TRADABLE_TOKENS } from '@uo/shared';
import { ApiError } from '../api/client';
import { configAPI, normalizeAnnouncements } from '../api/config';
import { usersAPI } from '../api/users';
import { useUiStore } from '../store/ui';
import { useAuthStore } from '../store/auth';
import { useActiveAccountStore, useViewAsAccountParams } from '../store/activeAccount';
import { AccountSwitcher, AccountSwitcherBar } from '../components/AccountSwitcher';
import { useWalletAuthLogin } from '../wallet/useWalletAuthLogin';
import {
  AssetOverview,
  DailyPerfCard,
  DailyPerfModal,
  ContractPanel,
  ConvertPanel,
  FeatureGrid,
  GlassPanel,
  NewsList,
  PriceTrendCard,
  QuickActions,
  TransferModal,
  UoAssetPanel,
  HangTradePanel,
  IdleLoginNoticeModal,
  WalletFuelTile,
  WalletHeader,
  WalletHero,
} from '../components/wallet';
import { BALANCE_CELL_KEYS, COLORS, TOPBAR_OFFSET, PROD_WALLET_SHOW_CONVERT, PROD_WALLET_SHOW_FEATURE_GRID, PROD_WALLET_SHOW_FUEL_TILE, PROD_WALLET_SHOW_NEWS, PROD_WALLET_SHOW_QUICK_ACTIONS } from '@/constants';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { formatWalletBalance } from '../utils/wallet-display';
import { mapStaticRewardPreview } from '../utils/static-reward-display';
import type { WalletDailyPerf } from '../utils/wallet-daily-perf';
import type { EpPricePoint } from '../utils/ep-price-series';

interface BalancesResponse {
  balances: Record<string, string>;
  ap_queue?: string;
  overview?: Record<string, string>;
  daily_perf?: WalletDailyPerf;
}

export function Wallet() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const openDrawer = useUiStore((s) => s.openDrawer);
  const navigate = useNavigate();
  const wallet = useWalletAuthLogin();
  const profile = useAuthStore((s) => s.profile);
  const viewAsSub = useActiveAccountStore((s) => s.accountId);
  const viewAsParams = useViewAsAccountParams();
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);

  const balanceCells = BALANCE_CELL_KEYS.map((key) => ({
    key,
    label: t(`wallet.balances.${key}`),
  }));

  const [assetsOpen, setAssetsOpen] = useState(true);
  const [contractOpen, setContractOpen] = useState(true);
  const [uoAssetOpen, setUoAssetOpen] = useState(true);
  const [perfModalPeriod, setPerfModalPeriod] = useState<'today' | 'yesterday' | null>(null);

  const [pairIdx, setPairIdx] = useState(0);
  const [pairDropdownOpen, setPairDropdownOpen] = useState(false);
  const [convAmount, setConvAmount] = useState('');
  const [convErr, setConvErr] = useState<string | null>(null);

  const [transferOpen, setTransferOpen] = useState(false);
  const [toUsername, setToUsername] = useState('');
  const [transferAsset, setTransferAsset] = useState<string>(TRADABLE_TOKENS[0] ?? 'uo');
  const [transferAssetDropdownOpen, setTransferAssetDropdownOpen] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferMemo, setTransferMemo] = useState('');
  const [transferTxPwd, setTransferTxPwd] = useState('');
  const [transferSecAnswer, setTransferSecAnswer] = useState('');
  const [transferShowSec, setTransferShowSec] = useState(false);
  const [transferErr, setTransferErr] = useState<string | null>(null);
  const [transferBusy, setTransferBusy] = useState(false);

  const masterId = profile?.id;
  const viewAccountKey = viewAsSub ?? 'main';

  const { data: bal, isError: balError, refetch: refetchBal } = useQuery({
    queryKey: ['user', 'balances', masterId, viewAccountKey],
    queryFn: () =>
      usersAPI.getBalances(viewAsSub ? { account_id: viewAsSub } : undefined) as Promise<BalancesResponse>,
    refetchInterval: 15_000,
    staleTime: 30_000,
    enabled: !!masterId,
  });

  const { data: masterBal } = useQuery({
    queryKey: ['user', 'balances', masterId, 'main'],
    queryFn: () => usersAPI.getBalances() as Promise<BalancesResponse>,
    staleTime: 60_000,
    enabled: !!masterId && viewAsSub !== null,
  });

  const { data: announcements } = useQuery({
    queryKey: ['announcements', 'ticker'],
    queryFn: () => configAPI.getAnnouncements(),
    enabled: PROD_WALLET_SHOW_NEWS,
  });

  const { data: staticPreview, isLoading: staticPreviewLoading } = useQuery({
    queryKey: ['user', 'static-reward', 'preview', masterId, viewAccountKey],
    queryFn: () => usersAPI.getStaticRewardPreview(viewAsParams),
    staleTime: 30_000,
    enabled: !!masterId && !!bal,
  });

  const { data: unitPriceSeries } = useQuery({
    queryKey: ['config', 'unit-price', 'series', 30],
    queryFn: () => configAPI.getUnitPriceSeries({ days: 30 }),
    staleTime: 60_000,
  });

  const uoAssetStatic = useMemo(() => mapStaticRewardPreview(staticPreview), [staticPreview]);
  const sparkData = useMemo<EpPricePoint[]>(
    () => unitPriceSeries?.series ?? [],
    [unitPriceSeries],
  );
  const subCount =
    Number((viewAsSub !== null ? masterBal?.overview?.sub_count : bal?.overview?.sub_count) ?? 0) || 0;
  const balances: Record<string, string> = {
    ...(bal?.overview ?? bal?.balances ?? {}),
  };
  if (bal?.ap_queue != null) balances.ap_queue = bal.ap_queue;
  if (viewAsSub === null && bal?.overview?.uo_total) {
    balances.uo_total = bal.overview.uo_total;
  }

  const announcementText = useMemo(() => {
    return normalizeAnnouncements(announcements)
      .map((a) => a.title)
      .join('　　　');
  }, [announcements]);

  function fmtBal(key: string) {
    return formatWalletBalance(balances[key]);
  }

  function closeTransferModal() {
    setTransferOpen(false);
    setTransferErr(null);
    setTransferTxPwd('');
    setTransferSecAnswer('');
    setTransferShowSec(false);
    setTransferAssetDropdownOpen(false);
  }

  async function submitTransfer() {
    if (!transferTxPwd.trim()) {
      setTransferErr(t('common.enterTxPassword'));
      return;
    }
    setTransferErr(null);
    setTransferBusy(true);
    try {
      await usersAPI.transfer({
        to_username: toUsername.trim(),
        asset: transferAsset,
        amount: transferAmount.trim(),
        memo: transferMemo.trim() || undefined,
        transaction_password: transferTxPwd.trim(),
        security_answer: transferSecAnswer.trim() || undefined,
      });
      setToUsername('');
      setTransferAmount('');
      setTransferMemo('');
      closeTransferModal();
      void qc.invalidateQueries({ queryKey: ['user'] });
    } catch (e) {
      setTransferErr(e instanceof ApiError ? e.message : t('transfer.error'));
    } finally {
      setTransferBusy(false);
    }
  }

  const convertMut = useMutation({
    mutationFn: () => {
      const pair = INTERNAL_CONVERT_PAIRS[pairIdx]!;
      return usersAPI.convert({
        from_asset: pair[0],
        to_asset: pair[1],
        amount: convAmount.trim(),
        idempotency_key: crypto.randomUUID().replace(/-/g, '').slice(0, 32),
      });
    },
    onSuccess: () => {
      setConvErr(null);
      setConvAmount('');
      void qc.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (e: unknown) => setConvErr(e instanceof ApiError ? e.message : t('convert.error')),
  });

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: COLORS.bg }}>
      <div className="relative mx-auto min-h-screen w-full max-w-[430px]">
        <WalletHeader
          t={t}
          onMenu={openDrawer}
          onConnect={() => void wallet.openWalletModal()}
          connectLabel={t('auth.connectShort', '连接')}
        />

        <main className="relative pb-2" style={{ paddingTop: TOPBAR_OFFSET }}>
          <WalletHero t={t} />

          <div className="relative z-10 -mt-3 flex flex-col gap-4 px-5">
            {(subCount > 0 || viewAsSub !== null) ? (
              <AccountSwitcherBar
                subCount={subCount}
                onOpen={() => setAccountPickerOpen(true)}
                onViewAll={() => navigate('/sub-accounts')}
              />
            ) : null}

            {balError && (
              <QueryErrorNote
                message={t('wallet.loadFailed')}
                onRetry={() => void refetchBal()}
              />
            )}

            <DailyPerfCard
              data={bal?.daily_perf}
              onOpen={(period) => setPerfModalPeriod(period)}
            />


            <AssetOverview
              t={t}
              open={assetsOpen}
              onToggle={() => setAssetsOpen((o) => !o)}
              cells={balanceCells}
              fmtBal={fmtBal}
              onPerfCellClick={(period) => setPerfModalPeriod(period)}
            />
            <DailyPerfModal
              open={perfModalPeriod !== null}
              period={perfModalPeriod}
              data={bal?.daily_perf}
              onClose={() => setPerfModalPeriod(null)}
            />

            {PROD_WALLET_SHOW_NEWS && announcementText && (
              <GlassPanel className="mb-3 px-3 py-2 text-[10px] text-white">
                <div className="ticker-scroll whitespace-nowrap">{announcementText}</div>
              </GlassPanel>
            )}

            {PROD_WALLET_SHOW_QUICK_ACTIONS && (
            <QuickActions
              t={t}
              onRecharge={() => navigate('/recharge-tp')}
              onWithdraw={() => navigate('/withdraw')}
              onTransfer={() => setTransferOpen(true)}
            />
            )}
            {PROD_WALLET_SHOW_FEATURE_GRID && <FeatureGrid t={t} onNavigate={navigate} />}
            <PriceTrendCard t={t} sparkData={sparkData} onViewMarket={() => navigate('/trading')} />
            {PROD_WALLET_SHOW_FUEL_TILE && <WalletFuelTile t={t} onNavigate={navigate} />}
            {PROD_WALLET_SHOW_NEWS && <NewsList t={t} rows={[]} onMore={() => navigate('/news')} />}
            <ContractPanel t={t} open={contractOpen} onToggle={() => setContractOpen((o) => !o)} balances={balances} />
            <UoAssetPanel
              t={t}
              open={uoAssetOpen}
              onToggle={() => setUoAssetOpen((o) => !o)}
              staticDisplay={uoAssetStatic}
              loading={staticPreviewLoading}
            />
            <HangTradePanel
              t={t}
              hangBuyIng={balances.hang_buy_ing}
              hangSellIng={balances.hang_sell_ing}
            />
            {PROD_WALLET_SHOW_CONVERT && (
            <ConvertPanel
              t={t}
              pairIdx={pairIdx}
              pairDropdownOpen={pairDropdownOpen}
              convAmount={convAmount}
              convErr={convErr}
              isLoading={convertMut.isPending}
              onToggleDropdown={() => setPairDropdownOpen((open) => !open)}
              onSelectPair={(index) => {
                setPairIdx(index);
                setPairDropdownOpen(false);
              }}
              onAmountChange={setConvAmount}
              onSubmit={() => convertMut.mutate()}
            />
            )}
          </div>
        </main>

        <TransferModal
          t={t}
          open={transferOpen}
          toUsername={toUsername}
          transferAsset={transferAsset}
          assetDropdownOpen={transferAssetDropdownOpen}
          transferAmount={transferAmount}
          transferMemo={transferMemo}
          transferTxPwd={transferTxPwd}
          transferSecAnswer={transferSecAnswer}
          showSecurity={transferShowSec}
          error={transferErr}
          busy={transferBusy}
          onClose={closeTransferModal}
          onUsernameChange={setToUsername}
          onAssetDropdownToggle={() => setTransferAssetDropdownOpen((open) => !open)}
          onAssetChange={(asset) => {
            setTransferAsset(asset);
            setTransferAssetDropdownOpen(false);
          }}
          onAmountChange={setTransferAmount}
          onMemoChange={setTransferMemo}
          onTxPwdChange={setTransferTxPwd}
          onSecurityAnswerChange={setTransferSecAnswer}
          onSecurityToggle={() => setTransferShowSec((v) => !v)}
          onSubmit={() => void submitTransfer()}
        />
      </div>
      <AccountSwitcher
        open={accountPickerOpen}
        onClose={() => setAccountPickerOpen(false)}
        subCount={subCount}
      />
      <IdleLoginNoticeModal />
    </div>
  );
}
